import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  ChevronRight,
  Key,
  Bell,
  Search,
  Shield,
  GraduationCap,
  Sparkles
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useInstitute } from "../../contexts/InstituteContext";
import { useAccess } from "../../contexts/AccessContext";
import Sidebar from "./Sidebar";
import StudentTour from "../tours/StudentTour";
import ChangePasswordModal from "../auth/ChangePasswordModal";
import { api } from "../../services/api";
import defaultLogo from "../../assets/navipro_logo.png";

const Layout = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const { institute, branding } = useInstitute();
  const { isPlatformSuperadmin } = useAccess();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const handleSearchNavigation = useCallback(() => {
    if (user?.role === 'instructor') navigate('/instructor/courses');
    else if (user?.role === 'admin' || user?.role === 'superadmin') navigate('/student-track/course-management');
    else navigate('/courses');
  }, [user, navigate]);

  // Global Ctrl + K search shortcut and Escape mobile drawer close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        handleSearchNavigation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, handleSearchNavigation]);

  useEffect(() => {
    let cancelled = false;

    const loadUnreadCount = async () => {
      if (!user) {
        setUnreadNotifications(0);
        return;
      }

      try {
        const response = await api.getUnreadNotificationCount();
        if (!cancelled) {
          setUnreadNotifications(response.data?.count || 0);
        }
      } catch {
        if (!cancelled) {
          setUnreadNotifications(0);
        }
      }
    };

    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, 30000);

    const handleNotificationsChanged = () => {
      loadUnreadCount();
    };

    window.addEventListener('notifications:changed', handleNotificationsChanged);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('notifications:changed', handleNotificationsChanged);
    };
  }, [user]);

  const isInstituteAdmin = user?.role === 'admin' && !isPlatformSuperadmin;
  const headerTitle = isInstituteAdmin ? (institute?.name || user?.full_name || 'Institute') : 'Navipro Nxt';

  const shouldShowSidebar = user && user.type !== 'paid_exam_user';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Determine logo redirect route per role
  const logoHref = (location.pathname.startsWith('/instructor') || user?.role === 'instructor')
    ? '/instructor/dashboard'
    : (user?.role === 'support'
      ? '/admin/exam-users'
      : ((user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin')
        ? '/admin/dashboard'
        : '/dashboard'));

  // Determine breadcrumb title
  const getBreadcrumbTitle = (pathname) => {
    if (pathname === '/dashboard') return 'Dashboard';
    if (pathname === '/admin/dashboard') return 'Admin Dashboard';
    if (pathname === '/instructor/dashboard' || pathname === '/instructor') return 'Mentor Dashboard';
    if (pathname === '/student-track/platform-dashboard') return 'Platform Dashboard';
    if (pathname.startsWith('/student-track/course-management') || pathname.startsWith('/instructor/courses')) return 'Course Management';
    if (pathname.startsWith('/courses')) return 'Courses';
    if (pathname.startsWith('/admin/programs') || pathname.startsWith('/instructor/programs')) return 'Batch Management';
    if (pathname.startsWith('/admin/runways') || pathname.startsWith('/instructor/runways')) return 'Runway Management';
    if (pathname.startsWith('/student-track/student-management') || pathname.startsWith('/instructor/students')) return 'Student Management';
    if (pathname.startsWith('/student-track/instructor-management')) return 'Mentor Management';
    if (pathname.startsWith('/live-classes')) return 'Live Classes';
    if (pathname.startsWith('/tests')) return 'Tests / Quiz';
    if (pathname.startsWith('/assignments') && !pathname.startsWith('/admin/assignments') && !pathname.startsWith('/instructor/assignments')) return 'Topic Exams';
    if (pathname.startsWith('/admin/assessments') || pathname.startsWith('/instructor/assessments') || pathname.startsWith('/student/assessments')) return 'Assignments';
    if (pathname.startsWith('/admin/assignments') || pathname.startsWith('/instructor/assignments')) return 'Exam Management';
    if (pathname.startsWith('/code-lab')) return 'Code Lab';
    if (pathname.startsWith('/bookmarks')) return 'Bookmarks';
    if (pathname.startsWith('/admin/payments') || pathname.startsWith('/student/payments')) return 'Payments';
    if (pathname.startsWith('/notifications-center')) return 'Notifications';
    if (pathname.startsWith('/profile')) return 'Profile';
    if (pathname.startsWith('/settings')) return 'Settings';
    return 'Dashboard';
  };

  // Early return for paid_exam_user users - simple exam view
  if (user?.type === 'paid_exam_user') {
    return (
      <div className="min-h-screen bg-[#F5F7FD] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#365cf5] mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading exam environment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FD] flex flex-row relative text-[#111827] antialiased">
      {/* Unified Role-Aware Sidebar */}
      {shouldShowSidebar && (
        <Sidebar
          isOpen={sidebarOpen}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          onNavItemClick={() => setSidebarOpen(false)}
          portalType={isPlatformSuperadmin ? 'superadmin' : (isInstituteAdmin ? 'institute' : 'default')}
          onLogout={handleLogout}
          unreadNotifications={unreadNotifications}
        />
      )}

      {/* Overlay for mobile drawer */}
      {shouldShowSidebar && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <StudentTour />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#F3F6FB] min-h-screen relative overflow-x-hidden">
        {/* Ambient soft sky-blue glow at top right matching target reference */}
        <div className="absolute top-0 right-0 w-[700px] h-[400px] bg-gradient-to-bl from-[#DBEAFE]/30 via-[#EEF2FF]/20 to-transparent pointer-events-none -z-0" />

        {/* Top Navbar matching V16 Q-TRAN Master Design */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#DCE4EF] h-[68px] sm:h-[72px] px-3 sm:px-6 flex items-center justify-between gap-2.5 sm:gap-4 min-w-0">
          {/* Left: Mobile Toggle + Logo + Compressible Breadcrumbs */}
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 shrink-0">
            {shouldShowSidebar && (
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-slate-600 hover:text-slate-900 hover:bg-slate-100 h-9 w-9 shrink-0"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle navigation drawer"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}

            {/* Single Approved Q-TRAN Brand Emblem & Wordmark (with White-Label Institute Support) */}
            <Link
              to={logoHref}
              className="flex items-center gap-2 select-none shrink-0 group"
              aria-label="Dashboard Home"
            >
              <img
                src={branding?.logo_url || defaultLogo}
                alt={institute?.name || headerTitle || "Q-TRAN LMS"}
                onError={(e) => {
                  if (e.currentTarget.src !== defaultLogo) {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultLogo;
                  }
                }}
                className="h-8 sm:h-9 w-auto max-w-[130px] sm:max-w-[170px] object-contain transition-transform group-hover:scale-[1.02]"
              />
            </Link>

            <span className="h-5 w-[1px] bg-[#DCE4EF] hidden sm:block shrink-0" aria-hidden="true" />

            {/* Breadcrumbs: Compresses gracefully on narrow screens */}
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#64748B] whitespace-nowrap min-w-0 max-w-[130px] md:max-w-[190px] lg:max-w-[260px] xl:max-w-none">
              <span className="text-[#64748B] font-medium hidden lg:inline truncate">
                {user?.role === 'instructor' ? 'Mentor portal' :
                 (user?.role === 'admin' || user?.role === 'superadmin') ? 'Admin console' :
                 'Student workspace'}
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 hidden lg:inline" />
              <strong className="text-[#0F172A] font-bold truncate">
                {getBreadcrumbTitle(location.pathname)}
              </strong>
            </div>
          </div>

          {/* Center: Search Field (Takes Center Stage: 300–520px when space permits) */}
          <div className="flex-1 flex items-center justify-center min-w-0 max-w-[520px] mx-1 sm:mx-3">
            <div
              onClick={handleSearchNavigation}
              className="hidden md:flex items-center justify-between w-full h-10 px-3.5 bg-slate-50/90 hover:bg-slate-100/90 border border-[#DCE4EF] hover:border-[#2563EB]/40 rounded-xl text-xs text-slate-500 cursor-pointer transition-all shadow-2xs group min-w-[220px]"
              title="Search courses, classes, topics..."
              role="search"
            >
              <div className="flex items-center gap-2.5 truncate min-w-0">
                <Search className="h-4 w-4 text-slate-400 group-hover:text-[#2563EB] transition-colors shrink-0" />
                <span className="truncate text-slate-500 font-medium">
                  {user?.role === 'student' ? 'Search courses, classes, topics...' : 'Search portal...'}
                </span>
              </div>
              <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-400 bg-white border border-[#DCE4EF] rounded px-1.5 py-0.5 shadow-2xs shrink-0 ml-2">
                Ctrl + K
              </kbd>
            </div>
          </div>

          {/* Right: Actions, Status Pill, Notifications & Avatar */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Mobile Search Icon Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden rounded-xl h-9 w-9 text-slate-600 hover:text-[#2563EB] hover:bg-slate-100 transition-colors cursor-pointer"
              onClick={handleSearchNavigation}
              title="Search"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </Button>

            {/* Role / Status Indicator Pill: 2xl only so narrower laptop screens don't squeeze search */}
            <div className="hidden 2xl:flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold text-[#475569] bg-[#F8FAFD] border border-[#DCE4EF] select-none shrink-0">
              {user?.role === 'instructor' ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-[#2563EB] ring-4 ring-blue-50 animate-pulse" />
                  <span className="text-[#2563EB] font-bold">Mentor Portal</span>
                </>
              ) : isPlatformSuperadmin ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-[#7C3AED] ring-4 ring-purple-50 animate-pulse" />
                  <span className="text-[#7C3AED] font-bold">Super Admin</span>
                </>
              ) : isInstituteAdmin ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-[#0284C7] ring-4 ring-sky-50 animate-pulse" />
                  <span className="text-[#0284C7] font-bold">Institute Admin</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-[#10B981] ring-4 ring-emerald-50 animate-pulse" />
                  <span>Learning in progress</span>
                </>
              )}
            </div>

            {/* Notification Bell */}
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-xl h-9 w-9 text-slate-600 hover:text-[#2563EB] hover:bg-slate-100 transition-colors cursor-pointer"
              onClick={() => navigate('/notifications-center')}
              title="Notifications"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
              {unreadNotifications > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
              )}
            </Button>

            {/* User Avatar + Name dropdown matching design reference */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1 sm:px-2 sm:py-1 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer select-none">
                  <Avatar className="h-8 w-8 rounded-full border border-slate-200 shadow-2xs">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-[#EEF2FF] text-[#3855F6] font-bold text-xs">
                      {getInitials(user?.full_name || user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:flex flex-col text-left leading-tight">
                    <span className="text-xs font-bold text-[#111827] truncate max-w-[120px]">
                      {user?.full_name || user?.name || "User"}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium capitalize">
                      {user?.role === 'instructor' ? 'Mentor' : (user?.role || "Student")}
                    </span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border border-[#E5E9F2] shadow-lg rounded-xl" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-semibold leading-none text-[#111827]">{user?.full_name || user?.name}</p>
                    <p className="text-xs leading-none text-slate-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowChangePasswordModal(true)} className="cursor-pointer">
                  <Key className="mr-2 h-4 w-4" />
                  <span>Change Password</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main content container */}
        <main className="flex-1 p-6 lg:p-8 max-w-[1600px] w-full mx-auto relative z-10">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePasswordModal} 
        onClose={() => setShowChangePasswordModal(false)} 
      />
    </div>
  );
};

export default Layout;
